import { CSSResultGroup, TemplateResult, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property } from 'lit/decorators.js';
import { LinkedBlockController } from '../../controllers/linked-block-controller';
import { SicCard } from '../sic-card/sic-card';
import '../sic-icon/sic-icon';

import componentStyles from './sic-card-linked.css.lit';
import defaultVars from './css-variables/sic-card-linked-vars.css.lit';

/**
 * The Sic Card Linked component
 * @element sic-card-linked
 *
 * @slot heading
 * @slot eyebrow
 * @slot description
 *
 * @attr external
 *
 * Lightdom styles in sic-card-linked.lightdom.css included in /shared.css
 */
@customElement('sic-card-linked')
export class SicCardLinked extends SicCard {
  static styles: CSSResultGroup = [componentStyles, defaultVars];

  /**
   * Is link external (open in new tab)
   **/
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'external',
  })
  external = false;

  /**
   * If there is not link.
   **/
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'no-link',
  })
  noLink = false;

  observeRootElement: Element | null | undefined = null;
  private _observeVisibility: IntersectionObserver | undefined;

  // eslint-disable-next-line
  // @ts-ignore
  // We are using the controller to manage link behavior, but don't need to use it otherwise.
  private _linkedBlockController = new LinkedBlockController(this, () => {
    return this.querySelector('[slot="heading"] a') ?? undefined;
  });

  firstUpdated() {
    // Add _observeVisibility if within sic-carousel.
    if (this.closest('sic-carousel')) {
      const carousel = this.closest('sic-carousel');
      if (carousel && carousel.shadowRoot) {
        /**
         * Initializes an MutationObserver to observe the visibility of elements.
         *
         * Check carousel for when splide track is available to be used in IntersectionObserver.
         *
         */
        const observer = new MutationObserver((mutationsList, observer) => {
          // Look through all mutations that just occured.
          for (const mutation of mutationsList) {
            // If the addedNodes property has one or more nodes.
            if (mutation.addedNodes.length && carousel && carousel.shadowRoot) {
              const targetNode =
                carousel.shadowRoot.querySelector('.splide__wrapper');
              if (targetNode) {
                this.observeRootElement = targetNode;
                /**
                 * Initializes an IntersectionObserver to observe the visibility of elements.
                 *
                 * If cards are utilized within carousels, our objective is to ensure that
                 * links that are not visible are set as nonfocusable.
                 *
                 */
                this._observeVisibility = new IntersectionObserver(
                  entries => {
                    entries.forEach(entry => {
                      let lastElement = false;
                      if (carousel.lastElementChild) {
                        lastElement =
                          carousel.lastElementChild.querySelector(
                            'sic-card-linked'
                          ) === entry.target;
                      }
                      if (
                        !entry.isIntersecting ||
                        entry.intersectionRatio < 0.95
                      ) {
                        this.querySelector('[slot="heading"] a')?.setAttribute(
                          'tabindex',
                          '-1'
                        );
                      } else {
                        this.querySelector(
                          '[slot="heading"] a'
                        )?.removeAttribute('tabindex');
                      }
                      // If last carousel element account for negative margin and make focusable.
                      if (lastElement && entry.intersectionRatio > 0.6) {
                        this.querySelector(
                          '[slot="heading"] a'
                        )?.removeAttribute('tabindex');
                      }
                    });
                  },
                  {
                    root: this.observeRootElement,
                    threshold: [0.05, 0.6, 0.95],
                  }
                );

                this._observeVisibility.observe(this);

                observer.disconnect(); // Stop observing when target node is found.
                break;
              }
            }
          }
        });

        // Start observing the document with the configured parameters.
        observer.observe(carousel.shadowRoot, {
          childList: true,
          subtree: true,
        });
      }
    }

    // Set the noLink attribute based on the presence of a link.
    if (!this.querySelector('[slot="heading"] a')) {
      this.noLink = true;
    }
  }

  render(): TemplateResult {
    const classes = this.mediaClasses();

    return html`
      <div class="card-wrapper ${classMap(classes)}">
        ${this.mediaTemplate()}
        <div slot="content" class="card-content">
          ${this.slots.conditionalSlot(
            'eyebrow',
            true,
            'card-eyebrow text-headline-eyebrow'
          )}
          ${this.slots.conditionalSlot('heading', false, 'card-heading')}
          ${this.slots.conditionalSlot('description', false)}

          <div
            class="link-indicator"
            role="note"
            aria-label=" ${ifDefined(
              this.external
                ? 'Clicking on this link will open a page in a new tab'
                : null
            )}"
          >
            ${this.external
              ? html`<sic-icon
                  aria-hidden="true"
                  name="external-link-alt"
                  size="16px"
                ></sic-icon>`
              : html`<sic-icon
                  aria-hidden="true"
                  name="chevron-right"
                  size="18px"
                ></sic-icon>`}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-card-linked': SicCardLinked;
  }
}
